import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import SiteFooter from "@/components/SiteFooter";
import { CardTile, money, shortMoney, guessCountry, saveCountry, useStoreCart, COUNTRY_CUR, brandImage, flyToCart, CartDock } from "@/lib/giftStore";
import {
    Search, ShoppingCart, X, Zap, ShieldCheck, Clock, Minus, Plus, Gift, ChevronDown, ChevronLeft, ChevronRight,
    Gamepad2, Tv, ShoppingBag, UtensilsCrossed, Plane, House, CreditCard, Flame, Tag, Trophy, Wallet, Mail, MessageCircle, Link2,
} from "lucide-react";

const CAT_STYLE = {
    "Gaming": { icon: Gamepad2, bg: "#ede9fe", fg: "#5b21b6", blurb: "Steam, PlayStation, Xbox" },
    "Entertainment": { icon: Tv, bg: "#ffe4e6", fg: "#be123c", blurb: "Streaming and music" },
    "Shopping": { icon: ShoppingBag, bg: "#fef3c7", fg: "#b45309", blurb: "Amazon, Walmart, Best Buy" },
    "Food & Drink": { icon: UtensilsCrossed, bg: "#dcfce7", fg: "#15803d", blurb: "Coffee and delivery" },
    "Travel": { icon: Plane, bg: "#e0f2fe", fg: "#0369a1", blurb: "Rides and trips" },
    "Home": { icon: House, bg: "#ffedd5", fg: "#c2410c", blurb: "Tools and home" },
    "Prepaid": { icon: CreditCard, bg: "#ccfbf1", fg: "#0f766e", blurb: "Prepaid cards" },
    "Other": { icon: Gift, bg: "#f1f5f9", fg: "#334155", blurb: "More gift cards" },
};
const PAY = [["wallet", "Wallet", Wallet], ["card", "Card", CreditCard], ["paypal", "PayPal", CreditCard], ["etransfer", "e-Transfer", Mail]];

// The cheapest available amount, with its real crossed-out price (card value, or the everyday price during a sale)
function fromDeal(p) {
    const av = (p.denominations || []).filter((d) => d.available);
    if (!av.length) return null;
    return av.reduce((best, d) => (d.price < best.price ? d : best), av[0]);
}

function bestOff(p) {
    const offs = (p.denominations || []).filter((d) => d.available && d.was).map((d) => Math.round((1 - d.price / d.was) * 100));
    return offs.length ? Math.max(...offs) : 0;
}

function saleEnds(p) {
    if (!p.sale?.until) return "";
    try { return new Date(`${p.sale.until}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return p.sale.until; }
}

function fromPrice(p) {
    const av = (p.denominations || []).filter((d) => d.available);
    if (av.length) return Math.min(...av.map((d) => d.price));
    if (p.custom) return p.custom.min;
    return null;
}

function deliveryOf(p) {
    if (p.sold_out) return { label: "Sold out", cls: "bg-black/60 text-white", icon: null };
    if ((p.denominations || []).some((d) => d.instant)) return { label: "Instant", cls: "bg-emerald-400 text-emerald-950", icon: Zap };
    return { label: "Fast", cls: "bg-white/90 text-slate-900", icon: Clock };
}

function ProductTile({ p, cur, onOpen, dark }) {
    const dl = deliveryOf(p);
    const from = fromPrice(p);
    const save = bestOff(p);
    const deal = fromDeal(p);
    return (
        <button onClick={() => onOpen(p)} data-testid={`store-product-${p.id}`}
            className={`group w-full text-left rounded-2xl p-2 transition-transform duration-150 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${dark ? "bg-white/5 hover:bg-white/10" : "bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-md"} ${p.sold_out ? "opacity-60" : ""}`}>
            <div className="relative">
                <CardTile brand={p.brand} name={p.name} color={p.color} image={p.image_url} />
                <div className="absolute right-2 top-2 flex flex-wrap justify-end gap-1">
                    {save > 0 && !p.sold_out && <span className="rounded-md bg-rose-600 px-1.5 py-0.5 text-[11px] font-bold text-white">{p.sale ? "SALE " : ""}-{save}%</span>}
                    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${dl.cls}`}>{dl.icon && <dl.icon size={11} />}{dl.label}</span>
                </div>
            </div>
            <div className="px-1 pt-2.5 pb-1">
                <div className={`font-semibold text-sm truncate ${dark ? "text-white" : "text-slate-900"}`}>{p.name}</div>
                <div className="mt-0.5 flex items-center justify-between gap-2 text-xs">
                    <span className={`truncate ${dark ? "text-slate-400" : "text-slate-500"}`}>{p.category}</span>
                    {from != null && (
                        <span className="whitespace-nowrap">
                            {deal?.was && deal.price === from && <s className={`mr-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>{shortMoney(cur, deal.was)}</s>}
                            <span className={`font-bold ${dark ? "text-emerald-300" : save > 0 ? "text-rose-600" : "text-emerald-700"}`}>{deal?.was ? "" : "from "}{shortMoney(cur, from)}</span>
                        </span>
                    )}
                </div>
                {p.sale && !p.sold_out && <div className={`mt-1 text-[11px] font-semibold ${dark ? "text-rose-300" : "text-rose-600"}`}>Sale ends {saleEnds(p)}</div>}
            </div>
        </button>
    );
}

function Row({ title, icon: I, items, cur, onOpen, note, dark }) {
    const ref = useRef(null);
    const scroll = (dir) => ref.current?.scrollBy({ left: dir * Math.max(300, ref.current.clientWidth * 0.8), behavior: "smooth" });
    if (!items.length) return null;
    const btn = dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50";
    return (
        <section aria-label={title}>
            <div className="flex items-end justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <h2 className={`flex items-center gap-2 text-lg sm:text-xl font-bold ${dark ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Outfit, ui-sans-serif" }}><I size={20} className={dark ? "text-emerald-300" : "text-emerald-700"} />{title}</h2>
                    {note && <p className={`text-sm mt-0.5 ${dark ? "text-slate-300" : "text-slate-500"}`}>{note}</p>}
                </div>
                <div className="hidden sm:flex gap-1.5">
                    <button onClick={() => scroll(-1)} aria-label={`Scroll ${title} left`} className={`w-9 h-9 rounded-full flex items-center justify-center ${btn}`}><ChevronLeft size={18} /></button>
                    <button onClick={() => scroll(1)} aria-label={`Scroll ${title} right`} className={`w-9 h-9 rounded-full flex items-center justify-center ${btn}`}><ChevronRight size={18} /></button>
                </div>
            </div>
            <div ref={ref} className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {items.map((p) => <div key={p.id} className="w-44 sm:w-52 shrink-0 snap-start"><ProductTile p={p} cur={cur} onOpen={onOpen} dark={dark} /></div>)}
            </div>
        </section>
    );
}

function HeroStack({ items, cur }) {
    // real card pictures first (uploaded in admin), then brand-colour cards
    const has = (p) => p.image_url || brandImage(p.brand, p.name);
    const pick = [...items.filter(has), ...items.filter((p) => !has(p))].slice(0, 3);
    while (pick.length < 3) pick.push([{ brand: "Steam", color: "#171a21" }, { brand: "PlayStation", color: "#003791" }, { brand: "Amazon", color: "#232f3e" }][pick.length]);
    const pos = ["left-[2%] top-[24%] -rotate-[8deg] z-10", "left-[25%] top-[6%] rotate-[2deg] z-20", "left-[48%] top-[28%] rotate-[8deg] z-30"];
    return (
        <div className="relative h-56 sm:h-72" aria-hidden="true">
            {pick.map((c, i) => (
                <div key={i} className={`absolute w-[46%] max-w-[270px] ${pos[i]} drop-shadow-[0_16px_18px_rgba(0,0,0,0.28)]`}>
                    <CardTile brand={c.brand} name={c.name} color={c.color} image={c.image_url} cur={cur} size="lg" />
                </div>
            ))}
        </div>
    );
}

function ProductSheet({ p, cur, eta, onClose, onAdd, shareUrl }) {
    const cardRef = useRef(null);
    const dens = (p.denominations || []).filter((d) => d.available);
    const [pick, setPick] = useState(dens[0] ? { value: dens[0].value, price: dens[0].price, was: dens[0].was, instant: dens[0].instant } : null);
    const [custom, setCustom] = useState("");
    const [qty, setQty] = useState(1);
    const [more, setMore] = useState(false);

    const customVal = Number(custom);
    const customOk = p.custom && custom !== "" && customVal >= p.custom.min && customVal <= p.custom.max;
    const customPrice = customOk ? Math.round((customVal * ((p.pay_pct ?? p.price_pct) / 100) + Number(p.fee_flat || 0)) * 100) / 100 : null;
    const customWas = customOk ? (p.sale ? Math.round((customVal * (p.price_pct / 100) + Number(p.fee_flat || 0)) * 100) / 100 : customVal) : null;
    const sel = custom !== "" ? (customOk ? { value: customVal, price: customPrice, was: customWas > customPrice ? customWas : null, instant: false } : null) : pick;
    const total = sel ? Math.round(sel.price * qty * 100) / 100 : 0;

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-label={p.name}>
            <div className="w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 z-10 flex items-center justify-between bg-white/90 backdrop-blur px-5 py-3 border-b border-slate-100">
                    <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.country === "CA" ? "Canada" : "USA"} · {cur} · {p.category}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {shareUrl && (
                            <button onClick={() => { navigator.clipboard?.writeText(shareUrl).then(() => toast.success("Link copied")).catch(() => toast.error("Could not copy")); }}
                                aria-label="Copy link to this card" className="h-9 px-3 rounded-full hover:bg-slate-100 flex items-center gap-1.5 text-sm font-medium text-slate-600">
                                <Link2 size={16} /> Copy link
                            </button>
                        )}
                        <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 p-5">
                    <div className="space-y-4">
                        <div ref={cardRef}><CardTile brand={p.brand} name={p.name} color={p.color} image={p.image_url} value={sel ? sel.value : null} cur={cur} size="lg" /></div>
                        <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-semibold text-slate-600">
                            <div className="rounded-xl bg-slate-50 py-2.5"><ShieldCheck size={16} className="mx-auto mb-1 text-emerald-600" />Encrypted code</div>
                            <div className="rounded-xl bg-slate-50 py-2.5">{sel?.instant ? <Zap size={16} className="mx-auto mb-1 text-emerald-600" /> : <Clock size={16} className="mx-auto mb-1 text-emerald-600" />}{sel?.instant ? "Instant" : eta}</div>
                            <div className="rounded-xl bg-slate-50 py-2.5"><Gift size={16} className="mx-auto mb-1 text-emerald-600" />Send as a gift</div>
                        </div>
                        {(p.description || p.how_to_redeem || p.terms) && (
                            <div className="rounded-2xl ring-1 ring-slate-200">
                                <button onClick={() => setMore((m) => !m)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
                                    Details and how to redeem <ChevronDown size={16} className={`transition-transform ${more ? "rotate-180" : ""}`} />
                                </button>
                                {more && (
                                    <div className="px-4 pb-4 space-y-3 text-sm text-slate-600 whitespace-pre-line">
                                        {p.description && <p>{p.description}</p>}
                                        {p.how_to_redeem && <div><div className="font-semibold text-slate-800 mb-1">How to redeem</div>{p.how_to_redeem}</div>}
                                        {p.terms && <div className="text-xs text-slate-500">{p.terms}</div>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="space-y-5">
                        <div>
                            <div className="text-sm font-semibold text-slate-800 mb-2">Choose an amount</div>
                            {dens.length === 0 && !p.custom && <p className="text-sm text-slate-500 rounded-xl bg-slate-50 p-3">Sold out for now. Check back soon.</p>}
                            <div className="grid grid-cols-3 gap-2">
                                {dens.map((d) => {
                                    const on = custom === "" && pick && pick.value === d.value;
                                    return (
                                        <button key={d.value} onClick={() => { setPick(d); setCustom(""); }}
                                            className={`rounded-xl px-2 py-3 text-center ring-1 transition-all ${on ? "ring-2 ring-emerald-500 bg-emerald-50" : "ring-slate-200 hover:ring-emerald-300"}`}>
                                            <div className="font-bold text-slate-900">{shortMoney(cur, d.value)}</div>
                                            {d.was ? (
                                                <div className="text-[11px]"><s className="text-slate-400 mr-1">{shortMoney(cur, d.was)}</s><span className="font-bold text-rose-600">{shortMoney(cur, d.price)}</span></div>
                                            ) : (
                                                <div className="text-[11px] text-slate-500">{d.price !== d.value ? `pay ${shortMoney(cur, d.price)}` : d.instant ? "instant" : ""}</div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {p.custom && (
                                <label className="mt-3 flex items-center gap-2 rounded-xl ring-1 ring-slate-200 px-3 py-2.5 focus-within:ring-2 focus-within:ring-emerald-500">
                                    <span className="text-sm text-slate-500 whitespace-nowrap">Other amount</span>
                                    <input inputMode="decimal" value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
                                        placeholder={`${p.custom.min} - ${p.custom.max}`} className="flex-1 min-w-0 bg-transparent text-right font-semibold outline-none" />
                                    <span className="text-sm text-slate-400">{cur}</span>
                                </label>
                            )}
                            {custom !== "" && !customOk && <p className="mt-1.5 text-xs text-rose-600">Enter {shortMoney(cur, p.custom.min)} to {shortMoney(cur, p.custom.max)}.</p>}
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-800">Quantity</span>
                            <div className="flex items-center gap-1 rounded-full ring-1 ring-slate-200 p-1">
                                <button aria-label="Less" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><Minus size={14} /></button>
                                <span className="w-8 text-center font-bold">{qty}</span>
                                <button aria-label="More" onClick={() => setQty((q) => Math.min(10, q + 1))} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><Plus size={14} /></button>
                            </div>
                        </div>
                        <div className="rounded-2xl bg-slate-950 text-white p-4">
                            <div className="flex items-center justify-between text-sm text-slate-300"><span>Card value</span><span>{sel ? money(cur, sel.value * qty) : "-"}</span></div>
                            {sel?.was && <div className="mt-1 flex items-center justify-between gap-2 text-sm text-rose-300"><span>You save{p.sale ? ` (sale ends ${saleEnds(p)})` : ""}</span><span className="whitespace-nowrap"><s className="text-slate-400 mr-2">{money(cur, sel.was * qty)}</s>-{money(cur, (sel.was - sel.price) * qty)}</span></div>}
                            <div className="mt-1 flex items-end justify-between"><span className="text-sm text-slate-300">You pay</span><span className="text-2xl font-extrabold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>{sel ? money(cur, total) : "-"}</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button disabled={!sel} onClick={() => onAdd(p, sel, qty, false, cardRef.current)}
                                className="h-12 rounded-xl ring-1 ring-slate-200 font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-40">Add to cart</button>
                            <button disabled={!sel} onClick={() => onAdd(p, sel, qty, true)} data-testid="store-buy-now"
                                className="h-12 rounded-xl bg-emerald-600 font-bold text-white hover:bg-emerald-700 disabled:opacity-40">Buy now</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Country from the address: /buy-giftcard/usa, /buy-giftcard/canada, or ?country=us / ?country=ca
function countryFromUrl(pathname, search) {
    if (/^\/buy-giftcard\/(usa|us)(\/|$)/i.test(pathname)) return "US";
    if (/^\/buy-giftcard\/(canada|ca)(\/|$)/i.test(pathname)) return "CA";
    const q = new URLSearchParams(search).get("country") || new URLSearchParams(search).get("region") || "";
    if (/^(us|usa)$/i.test(q)) return "US";
    if (/^(ca|canada)$/i.test(q)) return "CA";
    return null;
}
const COUNTRY_PATH = { US: "/buy-giftcard/usa", CA: "/buy-giftcard/canada" };
// Each card's own link: /buy-giftcard/usa/steam, /buy-giftcard/canada/amazon ...
export function cardSlug(p) {
    return String(p?.name || p?.brand || p?.id || "card").toLowerCase()
        .replace(/&/g, " and ").replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || String(p?.id || "card");
}
function itemFromUrl(pathname) {
    const m = pathname.match(/^\/buy-giftcard\/(?:usa|us|canada|ca)\/([^/]+)\/?$/i);
    return m ? decodeURIComponent(m[1]).toLowerCase() : null;
}

export default function BuyGiftCard() {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const urlCountry = countryFromUrl(pathname, search);
    const [country, setCountryRaw] = useState(() => urlCountry || guessCountry());
    const cur = COUNTRY_CUR[country];
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [sort, setSort] = useState("popular");
    const itemSlug = itemFromUrl(pathname);
    const gridRef = useRef(null);
    const { cart, add, count } = useStoreCart();

    const setCountry = (c) => {
        setCountryRaw(c); saveCountry(c);
        const target = open ? `${COUNTRY_PATH[c]}/${cardSlug(open)}` : COUNTRY_PATH[c];
        if (pathname !== target) navigate(target, { replace: true });
    };
    // A link like /buy-giftcard/usa always opens the USA store, and remembers it
    useEffect(() => {
        if (urlCountry) { setCountryRaw(urlCountry); saveCountry(urlCountry); }
    }, [urlCountry]);
    useEffect(() => { api.post("/track/buygiftcard", { event: "visit" }).catch(() => {}); }, []);
    useEffect(() => {
        setLoading(true);
        api.get(`/store/catalog?country=${country}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ products: [], categories: [], open: true, methods: {} }))
            .finally(() => setLoading(false));
    }, [country]);

    const products = useMemo(() => data?.products || [], [data]);
    const open = useMemo(() => {
        if (!itemSlug) return null;
        return products.find((p) => cardSlug(p) === itemSlug) || products.find((p) => String(p.id).toLowerCase() === itemSlug) || null;
    }, [products, itemSlug]);
    const cardPath = (p) => `${COUNTRY_PATH[country]}/${cardSlug(p)}`;
    const setOpen = (p) => {
        if (p) navigate(cardPath(p));
        else navigate(COUNTRY_PATH[country], { replace: true });
    };
    // Link to a card that isn't sold in this country: just show the store
    useEffect(() => {
        if (itemSlug && data && !loading && !open) {
            toast.message("That card isn't available here right now. Here are the other cards.");
            navigate(COUNTRY_PATH[country], { replace: true });
        }
    }, [itemSlug, data, loading, open, country, navigate]);
    const live = products.filter((p) => !p.sold_out);
    const cats = useMemo(() => (data?.categories || []).filter((c) => products.some((p) => p.category === c)), [data, products]);
    const gaming = live.filter((p) => p.category === "Gaming");
    const trending = [...live].sort((a, b) => (Number(b.featured) - Number(a.featured)) || ((b.sold || 0) - (a.sold || 0))).slice(0, 12);
    const deals = live.filter((p) => bestOff(p) > 0).sort((a, b) => bestOff(b) - bestOff(a));
    const heroPool = [...gaming, ...trending].filter((p, i, a) => a.findIndex((x) => x.id === p.id) === i);
    const heroCards = heroPool.slice(0, 6);
    const quick = trending.slice(0, 6);
    const list = useMemo(() => {
        const s = q.trim().toLowerCase();
        const out = products
            .filter((p) => cat === "All" || p.category === cat)
            .filter((p) => !s || `${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(s));
        const by = {
            popular: (a, b) => (b.sold || 0) - (a.sold || 0),
            az: (a, b) => a.name.localeCompare(b.name),
            price: (a, b) => (fromPrice(a) ?? 1e9) - (fromPrice(b) ?? 1e9),
        }[sort];
        return out.sort((a, b) => (Number(a.sold_out) - Number(b.sold_out)) || by(a, b));
    }, [products, q, cat, sort]);
    const cartTotal = (cart[cur] || []).reduce((s, x) => s + x.price * x.qty, 0);
    const methods = PAY.filter(([k]) => data?.methods?.[k]);
    const browsing = q.trim() || cat !== "All";

    const pickCat = (c) => { setCat(c); setTimeout(() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
    const onAdd = (p, sel, qty, buyNow, fromEl) => {
        add(cur, p, sel.value, sel.price, qty);
        api.post("/track/buygiftcard", { event: buyNow ? "buy_click" : "add_to_cart" }).catch(() => {});
        if (buyNow) { setOpen(null); navigate(`/buy-giftcard/cart?cur=${cur}`); return; }
        // fly the card into the cart dock, then close the window
        flyToCart(fromEl);
        setTimeout(() => setOpen(null), 120);
        toast.success(`${p.name} ${shortMoney(cur, sel.value)} added to cart`);
    };

    return (
        <div className="min-h-screen bg-white text-slate-900 overflow-x-clip">
            <Helmet>
                {open && <title>{`Buy ${open.name} Gift Card (${cur}) | GiftToCash`}</title>}
                {open && <meta name="description" content={`Buy a ${open.name} gift card in ${cur}. Pay with wallet, card${cur === "CAD" ? ", PayPal or Interac e-Transfer" : " or PayPal"}. Code by email.`} />}
                {open && <link rel="canonical" href={`https://gifttocash.com${cardPath(open)}`} />}
                {!open && <title>{urlCountry === "US" ? "Buy Gift Cards and Game Credit in the USA (USD) | GiftToCash"
                    : urlCountry === "CA" ? "Buy Gift Cards and Game Credit in Canada (CAD) | GiftToCash"
                    : "Buy Gift Cards and Game Credit in Canada and the USA | GiftToCash"}</title>}
                {!open && <meta name="description" content={urlCountry === "US"
                    ? "Buy Steam, PlayStation, Xbox, Nintendo, Amazon, Netflix and more US gift cards in USD. Pay with wallet, card or PayPal. Codes by email."
                    : urlCountry === "CA"
                        ? "Buy Steam, PlayStation, Xbox, Nintendo, Amazon, Netflix and more Canadian gift cards in CAD. Pay with wallet, card, PayPal or Interac e-Transfer. Codes by email."
                        : "Buy Steam, PlayStation, Xbox, Nintendo, Amazon, Netflix and more digital gift cards. Pay with wallet, card, PayPal or Interac e-Transfer. Codes by email."} />}
                {!open && urlCountry && <link rel="canonical" href={`https://gifttocash.com${COUNTRY_PATH[urlCountry]}`} />}
            </Helmet>
            <PublicNav />
            {open && <ProductSheet p={open} cur={cur} eta={data?.manual_eta || "within 30 minutes"} onClose={() => setOpen(null)} onAdd={onAdd} shareUrl={`https://gifttocash.com${cardPath(open)}`} />}

            {/* HERO */}
            <section className="bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
                    <div className="min-w-0">
                        <div className="inline-flex rounded-lg bg-white p-1 ring-1 ring-slate-200" role="tablist" aria-label="Country">
                            {[["CA", "Canada (CAD)"], ["US", "USA (USD)"]].map(([c, l]) => (
                                <button key={c} role="tab" aria-selected={country === c} onClick={() => setCountry(c)}
                                    className={`px-3 h-8 rounded-md text-sm font-semibold transition-colors ${country === c ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
                            ))}
                        </div>
                        <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight text-slate-900" style={{ fontFamily: "Outfit, ui-sans-serif" }}>
                            Gift cards for games, shopping and more.
                        </h1>
                        <p className="mt-3 text-lg text-slate-600 max-w-lg">Top up Steam, PlayStation or Xbox, or send a friend Amazon or Netflix. You get the code by email and in your account.</p>
                        <label className="mt-6 flex items-center gap-2 h-14 rounded-xl bg-white ring-1 ring-slate-300 px-4 focus-within:ring-2 focus-within:ring-emerald-600 max-w-xl">
                            <Search size={20} className="text-slate-400" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for a brand, like Steam or Amazon" aria-label="Search gift cards" className="flex-1 min-w-0 bg-transparent outline-none text-slate-900" />
                            {q && <button onClick={() => setQ("")} aria-label="Clear search" className="text-slate-400"><X size={18} /></button>}
                        </label>
                        {quick.length > 0 && (
                            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-slate-500">Popular:</span>
                                {quick.map((p) => (
                                    <button key={p.id} onClick={() => setOpen(p)} className="h-8 px-3 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-400 font-medium text-slate-700">{p.brand}</button>
                                ))}
                            </div>
                        )}
                        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                            <Link to={`/buy-giftcard/cart?cur=${cur}`} data-testid="store-cart-link" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold whitespace-nowrap">
                                <ShoppingCart size={18} /> Cart{count > 0 ? ` (${count}) · ${money(cur, cartTotal)}` : ""}
                            </Link>
                            {methods.length > 0 && <span className="text-sm text-slate-500">Pay with {methods.map(([, l]) => l).join(", ")}</span>}
                        </div>
                    </div>
                    <div className="hidden sm:block"><HeroStack items={heroCards} cur={cur} /></div>
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-4 py-8 space-y-10">
                {data && !data.open && <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-4 text-sm text-amber-900">The store is closed for a moment. You can browse, but checkout is paused.</div>}
                {data?.notice && <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-4 text-sm text-emerald-900">{data.notice}</div>}

                {cats.length > 0 && (
                    <section aria-label="Shop by category">
                        <h2 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "Outfit, ui-sans-serif" }}>Shop by category</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            {cats.map((c) => {
                                const st = CAT_STYLE[c] || CAT_STYLE.Other;
                                return (
                                    <button key={c} onClick={() => pickCat(c)} className="flex items-center gap-3 rounded-xl bg-white ring-1 ring-slate-200 hover:ring-slate-400 p-3 text-left">
                                        <span className="w-11 h-11 shrink-0 rounded-lg flex items-center justify-center" style={{ background: st.bg, color: st.fg }}><st.icon size={22} /></span>
                                        <span className="min-w-0"><span className="block font-semibold text-slate-900 truncate">{c}</span><span className="block text-xs text-slate-500 truncate">{st.blurb}</span></span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {Array.from({ length: 10 }).map((_, i) => <div key={i} className="rounded-2xl bg-white ring-1 ring-slate-200 p-2"><div className="aspect-[1.586] rounded-xl bg-slate-100 animate-pulse" /><div className="h-4 mt-3 w-2/3 rounded bg-slate-100 animate-pulse" /></div>)}
                    </div>
                ) : products.length === 0 ? (
                    <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-10 sm:p-16 text-center">
                        <div className="mx-auto w-14 h-14 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Gift size={26} /></div>
                        <h2 className="mt-4 text-2xl font-bold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>New cards are on the way</h2>
                        <p className="mt-2 text-slate-600 text-sm max-w-md mx-auto">We're adding {country === "CA" ? "Canadian" : "US"} gift cards right now. Try the other country or check back soon.</p>
                        <button onClick={() => setCountry(country === "CA" ? "US" : "CA")} className="mt-5 h-11 px-5 rounded-xl bg-slate-900 text-white font-bold">Show {country === "CA" ? "USA" : "Canada"} cards</button>
                    </div>
                ) : (
                    <>
                        {!browsing && gaming.length > 0 && (
                            <section className="rounded-2xl bg-slate-900 p-5 sm:p-7">
                                <Row dark title="For gamers" icon={Trophy} items={gaming} cur={cur} onOpen={setOpen} note="Steam, PlayStation, Xbox, Nintendo and Roblox credit" />
                            </section>
                        )}
                        {!browsing && <Row title="Popular right now" icon={Flame} items={trending} cur={cur} onOpen={setOpen} />}
                        {!browsing && <Row title="Deals" icon={Tag} items={deals} cur={cur} onOpen={setOpen} note="Pay less than the card is worth" />}

                        <section ref={gridRef} className="scroll-mt-24" aria-label="All gift cards">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                                <h2 className="text-lg sm:text-xl font-bold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>{cat === "All" ? "All gift cards" : cat}<span className="ml-2 text-sm font-medium text-slate-500">{list.length}</span></h2>
                                <div className="flex gap-2 items-center min-w-0">
                                    <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] min-w-0">
                                        {["All", ...cats].map((c) => (
                                            <button key={c} onClick={() => setCat(c)} className={`h-9 px-3.5 rounded-full text-sm font-medium whitespace-nowrap ${cat === c ? "bg-slate-900 text-white" : "bg-white ring-1 ring-slate-200 text-slate-700 hover:ring-slate-400"}`}>{c}</button>
                                        ))}
                                    </div>
                                    <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" className="h-9 rounded-full bg-white ring-1 ring-slate-200 px-3 text-sm font-medium text-slate-700 outline-none">
                                        <option value="popular">Popular</option><option value="az">A to Z</option><option value="price">Lowest price</option>
                                    </select>
                                </div>
                            </div>
                            {list.length === 0 ? (
                                <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-10 text-center text-slate-600 text-sm">Nothing matches "{q}". Try another name.</div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                    {list.map((p) => <ProductTile key={p.id} p={p} cur={cur} onOpen={setOpen} />)}
                                </div>
                            )}
                        </section>
                    </>
                )}

                <section className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5 sm:p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[[Zap, "Fast delivery", `Most codes arrive ${data?.manual_eta || "within 30 minutes"}. Some arrive right away.`],
                      [ShieldCheck, "Safe codes", "Your code is stored safely and only shown to you."],
                      [Gift, "Send as a gift", "Email a card to a friend with your own message."],
                      [MessageCircle, "Real people", "Our team in Canada answers if anything goes wrong."]].map(([I, t, d]) => (
                        <div key={t} className="flex gap-3">
                            <I size={22} className="text-emerald-700 shrink-0 mt-0.5" />
                            <div><div className="font-semibold">{t}</div><p className="text-sm text-slate-600">{d}</p></div>
                        </div>
                    ))}
                </section>
            </main>

            <CartDock cur={cur} to={`/buy-giftcard/cart?cur=${cur}`} />
            <div className="h-24" aria-hidden="true" />
            <SiteFooter />
        </div>
    );
}
